import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import Header from '@/components/layout/Header';
import SEOHead from '@/components/SEOHead';
import Footer from '@/components/layout/Footer';
import { Button } from '@/components/ui/button';
import { ArrowRight, Play } from 'lucide-react';
import { Link } from 'react-router-dom';
import LazyImage from '@/components/LazyImage';
import LazyVideo from '@/components/LazyVideo';

import heroImage from '@/assets/portfolio-corporate-rooftop.png';
import hairstylistImg from '@/assets/portfolio-hairstylist.png';
import medspaTreatmentImg from '@/assets/portfolio-medspa-treatment.png';
import corporateRooftopImg from '@/assets/portfolio-corporate-rooftop.png';
import conferenceImg from '@/assets/portfolio-conference.png';
import shootingCorporateImg from '@/assets/shooting-corporate.jpg';
import shootingConferenceImg from '@/assets/shooting-conference.jpg';

interface PortfolioVideo {
  id: string;
  name: string;
  url: string;
  category: string;
}

const getYouTubeEmbedUrl = (url: string): string | null => {
  const params = '?rel=0&modestbranding=1&playsinline=1&hd=1';
  const shortsMatch = url.match(/youtube\.com\/shorts\/([a-zA-Z0-9_-]+)/);
  if (shortsMatch) return `https://www.youtube-nocookie.com/embed/${shortsMatch[1]}${params}`;
  const watchMatch = url.match(/(?:youtube\.com\/watch\?v=|youtu\.be\/)([a-zA-Z0-9_-]+)/);
  if (watchMatch) return `https://www.youtube-nocookie.com/embed/${watchMatch[1]}${params}`;
  return null;
};

const Portfolio = () => {
  const [videos, setVideos] = useState<PortfolioVideo[]>([]);

  useEffect(() => {
    supabase
      .from('portfolio_videos')
      .select('*')
      .order('created_at', { ascending: false })
      .then(({ data }) => {
        if (data) setVideos(data as any);
      });
  }, []);

  const getCategoryVideos = (category: string) =>
    videos.filter((v) => v.category === category);

  const categories = [
    {
      key: 'medspa',
      label: 'Med Spa & Aesthetics',
      description: 'Premium content designed to attract high-ticket clientele.',
      photos: [
        { src: hairstylistImg, alt: 'Hair stylist working with client' },
        { src: medspaTreatmentImg, alt: 'Med spa laser treatment session' },
      ],
      bg: 'bg-background',
    },
    {
      key: 'professional',
      label: 'Professional Services',
      description: 'Authority-driven brand storytelling for advisors, consultants, and real estate professionals.',
      photos: [
        { src: corporateRooftopImg, alt: 'Corporate rooftop building' },
        { src: conferenceImg, alt: 'Conference booth interaction' },
        { src: shootingConferenceImg, alt: 'Professional conference coverage' },
      ],
      bg: 'bg-muted/30',
    },
    {
      key: 'corporate',
      label: 'Corporate & Industrial',
      description: 'Corporate production & brand documentation.',
      photos: [
        { src: shootingCorporateImg, alt: 'Corporate production' },
      ],
      bg: 'bg-background',
    },
    {
      key: 'surgeons_clinics',
      label: 'Surgeons & Clinics',
      description: 'Compelling visual content for surgical practices and clinical environments.',
      photos: [],
      bg: 'bg-muted/30',
    },
    {
      key: 'behind_the_scenes',
      label: 'Behind the Scenes',
      description: 'An inside look at our creative process and production days.',
      photos: [],
      bg: 'bg-background',
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <SEOHead
        title="Video Production Portfolio Columbus Ohio | Our Work | Vantage Point Media"
        description="Browse our portfolio of professional video production, brand photography, and social media content for med spas, corporate events, surgeons, and professionals across Columbus and Central Ohio."
        canonical="https://vantagepointmediagroup.com/portfolio"
        keywords="video production portfolio Columbus Ohio, videography portfolio Ohio, brand photography Columbus, social media content examples Columbus"
      />
      <Header />
      <main>
        {/* Hero Section */}
        <section className="relative min-h-[70vh] flex items-center overflow-hidden">
          <div className="absolute inset-0 z-0">
            <img src={heroImage} alt="Aerial corporate building" className="w-full h-full object-cover" />
            <div className="absolute inset-0 bg-gradient-to-r from-foreground/95 via-foreground/80 to-foreground/60" />
          </div>
          <div className="container relative z-10 mx-auto px-4 sm:px-6 lg:px-8 pt-32 pb-20">
            <div className="max-w-3xl">
              <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">Our Portfolio</span>
              <h1 className="font-display font-extrabold text-4xl sm:text-5xl lg:text-6xl text-background leading-tight mb-6">
                Visual Storytelling That Converts
                <span className="block text-primary mt-2">Clients Into Revenue</span>
              </h1>
              <p className="text-xl text-background/80 max-w-2xl leading-relaxed">
                A look at the brands we've elevated through cinematic content, strategic media, and premium production.
              </p>
            </div>
          </div>
        </section>

        {/* Category Sections */}
        {categories.map((cat) => {
          const catVideos = getCategoryVideos(cat.key);
          const hasPhotos = cat.photos.length > 0;
          const hasVideos = catVideos.length > 0;

          if (!hasPhotos && !hasVideos) return null;

          return (
            <section key={cat.key} className={`py-24 ${cat.bg}`}>
              <div className="container mx-auto px-4 sm:px-6 lg:px-8">
                <div className="max-w-2xl mb-12">
                  <span className="inline-block text-accent font-semibold text-sm uppercase tracking-wider mb-4">{cat.label}</span>
                  <h2 className="font-display font-bold text-3xl sm:text-4xl text-foreground mb-4">
                    {cat.description}
                  </h2>
                </div>

                {/* Photos */}
                {hasPhotos && (
                  <div className="mb-12">
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-6 flex items-center gap-2">
                      <span className="w-8 h-px bg-muted-foreground/40" />
                      Photography
                    </h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {cat.photos.map((photo, i) => (
                        <div key={i} className="rounded-2xl overflow-hidden shadow-medium group">
                          <LazyImage
                            src={photo.src}
                            alt={photo.alt}
                            className="w-full h-[350px] sm:h-[400px] object-cover group-hover:scale-105 transition-transform duration-500"
                          />
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Videos */}
                {hasVideos && (
                  <div>
                    <h3 className="text-sm font-semibold uppercase tracking-wider text-muted-foreground mb-6 flex items-center gap-2">
                      <span className="w-8 h-px bg-muted-foreground/40" />
                      <Play className="w-4 h-4" />
                      Video Production
                    </h3>
                    <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
                      {catVideos.map((v) => {
                        const embedUrl = getYouTubeEmbedUrl(v.url);
                        return (
                          <div key={v.id} className="rounded-2xl overflow-hidden shadow-medium group">
                            {embedUrl ? (
                              <iframe
                                src={embedUrl}
                                title={v.name}
                                allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                allowFullScreen
                                className="w-full h-[500px] sm:h-[550px]"
                              />
                            ) : (
                              <LazyVideo
                                src={v.url}
                                className="w-full h-[350px] sm:h-[400px] object-cover"
                              />
                            )}
                          </div>
                        );
                      })}
                    </div>
                  </div>
                )}
              </div>
            </section>
          );
        })}

        {/* CTA */}
        <section className="py-20 bg-gradient-hero">
          <div className="container mx-auto px-4 sm:px-6 lg:px-8 text-center">
            <h2 className="font-display font-bold text-3xl sm:text-4xl text-background mb-6">Ready to Elevate Your Brand?</h2>
            <p className="text-background/80 text-lg mb-8 max-w-xl mx-auto">Let's create content that drives real business results.</p>
            <Link to="/">
              <Button variant="hero" size="xl" className="group">
                Book Your Discovery Call
                <ArrowRight className="w-5 h-5 group-hover:translate-x-1 transition-transform" />
              </Button>
            </Link>
          </div>
        </section>
      </main>
      <Footer />
    </div>
  );
};

export default Portfolio;
